/*
 * Lemonade Training Oy copy rights all right reserved.
 * deleteMyAccount callable contract (F032 D25, LT-2003)
 *
 * The server refuses to delete an account that still owns a gym or pays for a
 * live membership. The refusal rule lives here so the dashboard and the
 * callable cannot disagree about which membership blocks.
 */

/**
 * "check" answers whether the caller could delete now and deletes nothing.
 * "delete" re-runs every check, then deletes the account.
 */
export const DELETE_MY_ACCOUNT_MODES = ["check", "delete"] as const;
export type DeleteMyAccountMode = (typeof DELETE_MY_ACCOUNT_MODES)[number];

export function isDeleteMyAccountMode(
  value: unknown,
): value is DeleteMyAccountMode {
  return (DELETE_MY_ACCOUNT_MODES as readonly unknown[]).includes(value);
}

/** The server rejects any other key, or a missing mode, with invalid-argument. */
export type DeleteMyAccountRequest = { mode: DeleteMyAccountMode };

export type DeleteMyAccountResponse =
  { outcome: "allowed" } | { outcome: "deleted" };

/** `details.reason` on a failed-precondition refusal. Add members; never rename. */
export const ACCOUNT_DELETION_REFUSAL_REASON = {
  OWNS_GYM: "owns_gym",
  HAS_LIVE_MEMBERSHIP: "has_live_membership",
  REQUIRES_RECENT_LOGIN: "requires_recent_login",
} as const;
export type AccountDeletionRefusalReason =
  (typeof ACCOUNT_DELETION_REFUSAL_REASON)[keyof typeof ACCOUNT_DELETION_REFUSAL_REASON];

/** "delete" needs a sign-in (token `auth_time`) no older than this. */
export const ACCOUNT_DELETION_RECENT_LOGIN_SECONDS = 300;

/**
 * Why one membership blocks deletion:
 * - cancel_first: live and not set to end; cancelling informs the gym.
 * - commitment_running: a commitment with payments still due, cancelled or
 *   not. Deleting would void them, so it waits for the term to end.
 * - payment_owed: past_due, unpaid, or incomplete (a first invoice awaiting
 *   3DS can still be paid after the account is gone); settle it first.
 */
export const ACCOUNT_DELETION_MEMBERSHIP_BLOCKS = [
  "cancel_first",
  "commitment_running",
  "payment_owed",
] as const;
export type AccountDeletionMembershipBlock =
  (typeof ACCOUNT_DELETION_MEMBERSHIP_BLOCKS)[number];

export type AccountDeletionOwnedGym = {
  boxID: string;
  name: string;
  /** No ownerUID is recorded; the caller is the gym's admin on their Users doc. */
  ownerUnrecorded: boolean;
};

export type AccountDeletionLiveMembership = {
  /** The Users/{uid}/Subscriptions doc id (the Stripe subscription id). */
  subscriptionId: string;
  /** Product name, "" when the mirror has none. */
  name: string;
  /** The gym's name, or null for a platform subscription or an unreadable gym. */
  gymName: string | null;
  block: AccountDeletionMembershipBlock;
  /** commitment_running only: ISO-8601 commitment end, null while finalizing. */
  commitmentEndsAt: string | null;
};

/** HttpsError `details` of a refusal. */
export type AccountDeletionRefusalDetails =
  | {
      reason: typeof ACCOUNT_DELETION_REFUSAL_REASON.OWNS_GYM;
      gyms: AccountDeletionOwnedGym[];
    }
  | {
      reason: typeof ACCOUNT_DELETION_REFUSAL_REASON.HAS_LIVE_MEMBERSHIP;
      memberships: AccountDeletionLiveMembership[];
    }
  | {
      reason: typeof ACCOUNT_DELETION_REFUSAL_REASON.REQUIRES_RECENT_LOGIN;
      maxAgeSeconds: number;
    };

/** Stripe statuses that can still bill or resume. */
export const ACCOUNT_DELETION_LIVE_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "unpaid",
  "paused",
  "incomplete",
] as const;

/** The fields of a Users/{uid}/Subscriptions doc the rule reads. */
export type AccountDeletionSubscriptionLike = {
  status?: unknown;
  cancel_at_period_end?: unknown;
  cancel_at?: unknown;
  commitment?: {
    cancelAtCommitmentEnd?: unknown;
    paymentMode?: unknown;
    paymentsLeft?: unknown;
  } | null;
  commitmentPending?: unknown;
};

/**
 * The block one subscription puts on deletion, or null. A commitment ignores
 * Stripe's own cancel fields: its end is the schedule's, recorded in the mirror.
 */
export function accountDeletionBlockOf(
  sub: AccountDeletionSubscriptionLike | null | undefined,
): AccountDeletionMembershipBlock | null {
  if (!sub || typeof sub.status !== "string") return null;
  if (
    !(ACCOUNT_DELETION_LIVE_STATUSES as readonly string[]).includes(sub.status)
  ) {
    return null;
  }
  if (
    sub.status === "past_due" ||
    sub.status === "unpaid" ||
    sub.status === "incomplete"
  ) {
    return "payment_owed";
  }

  const mirror =
    sub.commitment && typeof sub.commitment === "object"
      ? sub.commitment
      : null;
  if (mirror || sub.commitmentPending === true) {
    const upfront = mirror?.paymentMode === "upfront";
    const noneLeft =
      typeof mirror?.paymentsLeft === "number" && mirror.paymentsLeft <= 0;
    if (!upfront && !noneLeft) return "commitment_running";
    return mirror?.cancelAtCommitmentEnd === true ? null : "cancel_first";
  }

  const ending =
    sub.cancel_at_period_end === true ||
    (sub.cancel_at !== null && sub.cancel_at !== undefined);
  return ending ? null : "cancel_first";
}

/**
 * The sentence for one blocking membership, shared by the refusal message and
 * the dashboard. `commitmentEnd` is the end date already formatted for the
 * reader (the server and the browser format it differently).
 */
export function accountDeletionMembershipSentence(
  m: Pick<AccountDeletionLiveMembership, "name" | "gymName" | "block">,
  commitmentEnd: string | null,
): string {
  const what = `${m.name || "Your membership"}${m.gymName ? ` at ${m.gymName}` : ""}`;
  if (m.block === "payment_owed") {
    return `${what} has a payment that isn't settled. Update your card in Memberships, or contact your gym.`;
  }
  if (m.block === "commitment_running") {
    const runs = commitmentEnd
      ? `runs until ${commitmentEnd}, with payments still due`
      : "still has payments due";
    return `${what} is a commitment that ${runs}. You can delete your account once it ends. To end it sooner, contact your gym.`;
  }
  return `${what} is still active. Cancel it in Memberships first, so your gym knows and nothing is charged after your account is gone. Questions? Contact your gym.`;
}

/** The sentence for owning gyms, shared by the refusal message and the dashboard. */
export function accountDeletionOwnsGymSentence(
  gyms: Pick<AccountDeletionOwnedGym, "name">[],
): string {
  const names = gyms.map((g) => g.name || "a gym").join(" and ");
  return `You own ${names}. Before you delete your account, someone else needs to become the owner, so your members keep their gym, bookings and payments. Contact us and we'll move the gym to the new owner with you.`;
}

export const ACCOUNT_DELETION_RECENT_LOGIN_SENTENCE =
  "For your safety, deleting your account needs a fresh sign-in. Sign in again, then try once more. Nothing has been deleted.";
